import { useContext, useEffect, useState } from "react";
import fetchStatus from "src/utils/api/fetch-status";
import { ActiveDate } from "src/scenes/dashboard/date-provider";
import { useActiveIntegrationsTeams } from "src/scenes/teams/dashboard/active-integrations-provider-teams";
import { addDays, format } from "date-fns";
import useAbortableStreamFetch from "src/utils/request/use-abortable-stream-fetch";
import { useAuth } from "src/components/providers/auth-context";

const useDashboardApi = (
    requestMethodMock,
    requestMethodActual,
    _requiredIntegrations,
    streamURL,
    options,
    queryParams
) => {
    const { fromDate, toDate } = useContext(ActiveDate);
    const { activeIntegrations: activeTeamIntegrations } = useActiveIntegrationsTeams();
    const { isDemo, userHasTeamsSubscription } = useAuth();

    const [state, setState] = useState({
        status: fetchStatus.PENDING,
        data: null,
        errorCode: null,
        string: "",
    });

    const fetchFromApi = async (requestMethod, controller) => {
        try {
            const response = await requestMethod({
                fromDate: fromDate && format(fromDate, "yyyy-MM-dd"),
                toDate: toDate && format(addDays(toDate, 1), "yyyy-MM-dd"),
                queryParams,
                abortController: controller,
            });
            setState((prevState) => ({
                ...prevState,
                status: fetchStatus.SUCCESS,
                data: response,
                errorCode: null,
                controller: null,
            }));
        } catch (e) {
            setState((prevState) => ({
                ...prevState,
                status: fetchStatus.ERROR,
                data: null,
                errorCode: e.status,
                controller: null,
            }));
        }
    };

    // eslint-disable-next-line consistent-return
    const fetchEffect = (controller) => {
        const actualController = controller || new AbortController();
        setState({
            status: fetchStatus.PENDING,
            data: null,
            errorCode: null,
            string: "",
            controller: actualController,
        });
        if (isDemo || !userHasTeamsSubscription)
            return fetchFromApi(requestMethodMock, actualController);
        if (activeTeamIntegrations.status === fetchStatus.SUCCESS) {
            if (!activeTeamIntegrations.data.integrations.find((int) => int.connected)) {
                return fetchFromApi(requestMethodMock, actualController);
            }
            if (activeTeamIntegrations.data.integrations.find((int) => int.connected)) {
                if (streamURL) {
                    setState((prevState) => ({
                        ...prevState,
                        data: null,
                        string: "",
                    }));
                    return useAbortableStreamFetch(
                        streamURL,
                        state,
                        actualController,
                        setState,
                        fromDate,
                        toDate,
                        options,
                        queryParams
                    );
                }
                return fetchFromApi(requestMethodActual, actualController);
            }
            return fetchFromApi(requestMethodMock, actualController);
        }
    };

    const refetch = (controller) => {
        if (state.controller) {
            state.controller.abort("unmounted");
        }
        setState((prevState) => ({
            ...prevState,
            status: fetchStatus.REFETCHING,
            controller,
        }));
        return fetchEffect(controller);
    };

    useEffect(() => {
        const controller = new AbortController();
        refetch(controller);
        return () => {
            if (controller) {
                controller.abort("unmounted");
            }
        };
    }, [activeTeamIntegrations, fromDate, toDate, streamURL, queryParams]);

    return {
        ...state,
        refetch,
    };
};

export default useDashboardApi;
